import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { AuthSelectors } from '@states/auth/auth.selectors';
import { filter, map, take } from 'rxjs/operators';

/**
 * Organization guard - protects host-only routes
 * - Redirects unauthenticated users to sign-in
 * - Redirects users without an organization to become-a-host
 * - Allows users with a primary organization
 * - Allows admins even without an organization (they can switch to any org)
 *
 * Waits for auth to initialize before checking (handles SSR/prerender reload case)
 */
export const hasOrganizationGuard: CanActivateFn = (_route, state) => {
  const store = inject(Store);
  const router = inject(Router);

  // Wait until auth has been initialized (Firebase onAuthStateChanged resolved)
  return store.select(AuthSelectors.isInitialized).pipe(
    filter((initialized) => initialized),
    take(1),
    map(() => {
      const user = store.selectSnapshot(AuthSelectors.getUser);

      console.log('[OrganizationGuard] Checking access:', {
        hasUser: !!user,
        userId: user?.id,
        organizationId: user?.organizationId,
        isAdmin: store.selectSnapshot(AuthSelectors.isAdmin),
        fullUser: user,
      });

      // Not authenticated - redirect to sign-in
      if (!user) {
        console.warn('[OrganizationGuard] No user in state, redirecting to sign-in');
        return router.createUrlTree(['/sign-in'], {
          queryParams: { returnUrl: state.url },
        });
      }

      // Platform admins (ID-token claim, HMM-283) can access even without an
      // organization — they can switch to any org
      if (store.selectSnapshot(AuthSelectors.isAdmin) === true) {
        console.log('[OrganizationGuard] Access granted - user is admin');
        return true;
      }

      // No organization - redirect to become-a-host
      if (!user.organizationId) {
        console.warn('[OrganizationGuard] Access denied: User has no organization', {
          userId: user.id,
          organizationId: user.organizationId,
        });
        return router.createUrlTree(['/become-a-host']);
      }

      // Has organization - allow access
      console.log('[OrganizationGuard] Access granted - user has organization');
      return true;
    }),
  );
};
